import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { IconCheck, IconClock, IconAlertCircle } from '@tabler/icons-react';
import { useCart } from '../context/CartContext';
import { useSiteContent } from '../context/SiteContentContext';
import { fetchOrderStatus, CHECKOUT_FORM_KEY } from '../lib/storeData';
import PageTitle from '../components/ui/PageTitle';
import './CheckoutPage.css';

var MAX_TRIES = 10;     // 10 checks...
var RETRY_MS = 3000;    // ...3 seconds apart = about 30 seconds

function describeItem(item) {
  var options = [item.variant, item.colour].filter(Boolean).join(', ');
  return item.name + (options ? ' (' + options + ')' : '') + ' \u00d7 ' + item.quantity;
}

export default function CheckoutSuccessPage() {
  var [searchParams] = useSearchParams();
  var sessionId = searchParams.get('session_id') || '';
  var cart = useCart();
  var content = useSiteContent();
  var c = content.c;

  // checking | paid | slow | expired | cancelled | not_found | unavailable
  var [view, setView] = useState('checking');
  var [order, setOrder] = useState(null);

  // Held in a ref so the check below runs once, not on every re-render
  var clearCartRef = useRef(cart.clearCart);
  clearCartRef.current = cart.clearCart;

  useEffect(function() {
    var stopped = false;
    var timer = null;
    var tries = 0;

    if (!sessionId) {
      setView('not_found');
      return;
    }

    function check() {
      tries++;
      fetchOrderStatus(sessionId).then(function(res) {
        if (stopped) return;

        if (res.error === 'not_found') {
          setView('not_found');
          return;
        }
        if (res.error) {
          if (tries < MAX_TRIES) { timer = setTimeout(check, RETRY_MS); } else { setView('unavailable'); }
          return;
        }

        setOrder(res.order);
        var state = res.order.state;

        if (state === 'paid') {
          clearCartRef.current();
          try { sessionStorage.removeItem(CHECKOUT_FORM_KEY); } catch (e) { /* storage blocked: nothing to clear */ }
          setView('paid');
        } else if (state === 'expired') {
          setView('expired');
        } else if (state === 'cancelled') {
          setView('cancelled');
        } else if (tries < MAX_TRIES) {
          setView('checking');
          timer = setTimeout(check, RETRY_MS);
        } else {
          setView('slow');
        }
      });
    }

    check();
    return function() {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId]);

  var leadTime = c('lead_time_standard', '2\u20133 weeks');

  function iconFor(kind) {
    if (kind === 'ok') return <div className="co-confirm-icon"><IconCheck size={28} /></div>;
    if (kind === 'wait') return <div className="co-confirm-icon co-confirm-icon--wait"><IconClock size={28} /></div>;
    return <div className="co-confirm-icon co-confirm-icon--warn"><IconAlertCircle size={28} /></div>;
  }

  var body = null;

  if (view === 'checking') {
    body = (
      <div className="co-confirm">
        {iconFor('wait')}
        <h2 className="co-confirm-title">{'Confirming your payment\u2026'}</h2>
        <p className="co-confirm-sub">{"This usually takes a few seconds. Please don't close this page."}</p>
      </div>
    );
  }

  if (view === 'paid' && order) {
    body = (
      <div className="co-confirm">
        {iconFor('ok')}
        <h2 className="co-confirm-title">Order confirmed!</h2>
        <p className="co-confirm-sub">
          {'Thank you \u2014 your order is in. Andre will be in touch at ' + order.email + ' if he has any questions.'}
        </p>
        <div className="co-confirm-details">
          <div className="co-confirm-row"><span>Order number</span><strong>{order.orderNumber}</strong></div>
          {order.items.map(function(item, i) {
            return <div className="co-confirm-row" key={i}><span>{i === 0 ? 'Items' : ''}</span><strong>{describeItem(item)}</strong></div>;
          })}
          <div className="co-confirm-row"><span>Total paid</span><strong>{'$' + (order.totalCents / 100).toFixed(2)}</strong></div>
          <div className="co-confirm-row"><span>Estimated lead time</span><strong>{leadTime}</strong></div>
        </div>
        <div className="co-confirm-next">
          <h3>What happens next</h3>
          <p>{'Andre begins crafting your bag within 48 hours. Every bag is made by hand, so it takes about ' + leadTime + '. Keep your order number handy if you need to get in touch.'}</p>
        </div>
        <div className="co-confirm-btns">
          <Link to="/" className="co-next">Continue shopping</Link>
        </div>
      </div>
    );
  }

  if (view === 'slow') {
    body = (
      <div className="co-confirm">
        {iconFor('wait')}
        <h2 className="co-confirm-title">{"We're still confirming your payment"}</h2>
        <p className="co-confirm-sub">
          {"Some payments take a little longer to clear. Please don't pay again."}
        </p>
        {order && (
          <div className="co-confirm-details">
            <div className="co-confirm-row"><span>Order number</span><strong>{order.orderNumber}</strong></div>
          </div>
        )}
        <div className="co-confirm-next">
          <p>{'If your payment goes through, Andre will be in touch' + (order ? ' at ' + order.email : '') + '. If you are unsure, contact Andre with your order number and he will check for you.'}</p>
        </div>
        <div className="co-confirm-btns">
          <Link to="/contact" className="co-next">Contact Andre</Link>
        </div>
      </div>
    );
  }

  if (view === 'expired') {
    body = (
      <div className="co-confirm">
        {iconFor('warn')}
        <h2 className="co-confirm-title">This payment page expired</h2>
        <p className="co-confirm-sub">{'Nothing was charged. Your bag is still saved, so you can check out again.'}</p>
        <div className="co-confirm-btns">
          <Link to="/checkout" className="co-next">Return to checkout</Link>
        </div>
      </div>
    );
  }

  if (view === 'cancelled') {
    body = (
      <div className="co-confirm">
        {iconFor('warn')}
        <h2 className="co-confirm-title">This order was cancelled</h2>
        <p className="co-confirm-sub">{'If you have any questions about it, please contact Andre' + (order ? ' with order number ' + order.orderNumber : '') + '.'}</p>
        <div className="co-confirm-btns">
          <Link to="/contact" className="co-next">Contact Andre</Link>
        </div>
      </div>
    );
  }

  if (view === 'not_found') {
    body = (
      <div className="co-confirm">
        {iconFor('warn')}
        <h2 className="co-confirm-title">{"We couldn't find this order"}</h2>
        <p className="co-confirm-sub">{'If you have just paid, please contact Andre and he will check for you.'}</p>
        <div className="co-confirm-btns">
          <Link to="/contact" className="co-next">Contact Andre</Link>
        </div>
      </div>
    );
  }

  if (view === 'unavailable') {
    body = (
      <div className="co-confirm">
        {iconFor('warn')}
        <h2 className="co-confirm-title">{"We couldn't check your order right now"}</h2>
        <p className="co-confirm-sub">{"If you paid, your payment is safe. Please don't pay again. Try refreshing this page in a minute, or contact Andre."}</p>
        <div className="co-confirm-btns">
          <Link to="/contact" className="co-next">Contact Andre</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="co-page">
      <PageTitle title={view === 'paid' ? 'Order confirmed' : 'Your order'} />
      <nav className="co-nav"><Link to="/" className="co-logo">Andre <span>M</span> Leather</Link></nav>
      <div className="container container--reading co-success">
        {body}
      </div>
    </div>
  );
}